import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "../../core/translation.core";
import CategoryInput from "./categoryInput.comp";
import { useQuery } from "@tanstack/react-query";
import { getSingleCategory } from "../../core/category.core";
import localeCore from "../../core/locale.core";
import CloseIcon from "../../icons/iconClose.comp";
import { RefObject, useRef } from "react";
import SolidWeakBtn from "../form/solidWeakBtn.comp";

export default function MultipleCategoriesSelector({
  methods,
  fieldName,
  buttonText,
  btnId,
}: {
  methods: UseFormReturn<any, any>;
  fieldName: string;
  buttonText?: string;
  btnId?: string;
}) {
  const { t, msg } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  function deleteCategory(catId: string) {
    const catIds = methods.getValues(fieldName);

    const set = new Set(catIds);
    set.delete(catId);
    methods.setValue(fieldName, Array.from(set));
  }

  return (
    <div className="flex flex-col gap-2">
      {/* <label>{t(msg.discount.attachToCategories)}</label> */}
      <AttachBadges methods={methods} fieldName={fieldName} handleDelete={deleteCategory} />
      <button
        type="button"
        id={btnId}
        className="bg-her-blue px-4 py-2 rounded-md text-white w-fit"
        onClick={() => dialogRef.current?.showModal()}
      >
        {buttonText ?? t(msg.category.add)}
      </button>
      <CategorySelectorDialog dialogRef={dialogRef} methods={methods} fieldName={fieldName} />
    </div>
  );
}

const AttachBadges = ({
  methods,
  fieldName,
  handleDelete,
}: {
  fieldName: any;
  methods: UseFormReturn<any, any>;
  handleDelete: (productId: string) => void;
}) => {
  const attachIds = useWatch({
    control: methods.control,
    name: fieldName,
  });

  if (!attachIds) return null;

  return (
    <div className="flex gap-2 flex-wrap">
      {attachIds.map((p) => (
        <AttachCategoryBadge key={p} attachId={p} handleDelete={handleDelete} />
      ))}
    </div>
  );
};

const AttachCategoryBadge = ({ attachId, handleDelete }: { attachId: string; handleDelete: (attachId: string) => void }) => {
  const { data: category, isFetched: categoryIsFetched } = useQuery({
    queryKey: ["category", attachId],
    queryFn: (context) => getSingleCategory(context.queryKey[1]),
    enabled: !!attachId,
  });

  if (!categoryIsFetched) return null;

  return (
    <div className="flex items-center px-5 py-2.5 text-sm font-medium text-center text-her-blue border border-her-blue rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300">
      {category?.name ? localeCore.getLocaleValueDefault(category?.name) : category?.code}
      <button
        className="flex items-center justify-center w-4 h-4 ml-2 text-xs font-semibold text-blue-800 bg-blue-200 rounded-full hover:bg-red-400 hover:text-white"
        onClick={(e) => {
          e.preventDefault();
          handleDelete(attachId);
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

interface CategorySelectorDialogProps {
  dialogRef: RefObject<HTMLDialogElement>;
  methods: UseFormReturn<any, any>;
  fieldName: string;
}
function CategorySelectorDialog({ dialogRef, methods, fieldName }: CategorySelectorDialogProps) {
  const { t, msg } = useTranslation();

  return (
    <dialog
      id="add-multiple-cats-dialog"
      ref={dialogRef}
      className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg w-[80vw] max-w-[700px] fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <h1 className="text-xl mb-4">{t(msg.delivery.assign)}</h1>
      <div className="flex flex-col gap-2">
        <CategoryInput register={() => methods.register(fieldName)} />
      </div>
      <div className="flex gap-4 mt-6">
        <SolidWeakBtn
          id="close-multiple-cats-dialog-btn"
          type="button"
          onClick={() => dialogRef.current?.close()}
          ariaLabel={t(msg.form.close)}
        >
          {t(msg.form.close)}
        </SolidWeakBtn>
      </div>
    </dialog>
  );
}
